import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  ACCOUNT_TYPES,
  ACCOUNT_TYPE_LABELS,
  createAccount,
  updateAccount,
  type Account,
  type AccountType,
} from '@/features/accounting/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

/**
 * Add or edit one account.
 *
 * Type is fixed after creation, and the field is disabled on edit to say so before the
 * server does. Moving an account between types would silently re-sign every balance ever
 * posted to it, so the API refuses it outright once anything has been posted — and
 * offering the control anyway would only teach people to expect it to work.
 */

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Enter a code')
    .max(20, 'That code is too long')
    .regex(/^[A-Za-z0-9.-]+$/, 'Digits, letters, dots and dashes only'),
  name: z.string().trim().min(1, 'Enter a name').max(150, 'That name is too long'),
  type: z.enum(ACCOUNT_TYPES),
  parent_id: z.string(),
  is_postable: z.boolean(),
  is_active: z.boolean(),
  description: z.string().trim().max(500, 'That description is too long'),
})

type FormValues = z.infer<typeof schema>

export function AccountFormDialog({
  open,
  account,
  accounts,
  onClose,
}: {
  open: boolean
  /** Absent for a new account. */
  account?: Account
  /** The whole chart, for the parent picker. */
  accounts: Account[]
  onClose: () => void
}) {
  const isEdit = account !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: account?.code ?? '',
      name: account?.name ?? '',
      type: account?.type ?? 'asset',
      parent_id: account?.parent_id ?? '',
      is_postable: account?.is_postable ?? true,
      is_active: account?.is_active ?? true,
      description: account?.description ?? '',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  // Its own descendants cannot be its parent, and neither can it. Only the account being
  // edited is excluded here — the chart is small enough that a deeper cycle check is not
  // worth the code, and the API rejects one anyway.
  const parents = accounts.filter((candidate) => candidate.id !== account?.id)

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const parent_id = values.parent_id || null
      const description = values.description || null
      if (account) {
        return updateAccount(account.id, {
          code: values.code,
          name: values.name,
          parent_id,
          is_postable: values.is_postable,
          is_active: values.is_active,
          description,
        })
      }
      return createAccount({
        code: values.code,
        name: values.name,
        type: values.type as AccountType,
        parent_id,
        is_postable: values.is_postable,
        description,
      })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success(isEdit ? `Saved ${saved.code} ${saved.name}.` : `Added ${saved.code}.`)
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.status === 409
            ? 'An account with that code already exists.'
            : error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${account.code}` : 'New account'}
        description={
          isEdit
            ? 'The account type cannot change once the account exists.'
            : 'Where in the chart it sits, and whether entries may be posted to it.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="account-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Add account'}
            </Button>
          </>
        }
      >
        <form id="account-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <div className="grid gap-4 tablet:grid-cols-[8rem_1fr]">
            <Field label="Code" error={errors.code?.message}>
              {(props) => (
                <Input {...props} {...register('code')} autoComplete="off" placeholder="1100" />
              )}
            </Field>
            <Field label="Name" error={errors.name?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('name')}
                  autoComplete="off"
                  placeholder="Accounts Receivable"
                />
              )}
            </Field>
          </div>

          <Field
            label="Type"
            error={errors.type?.message}
            hint={isEdit ? 'Fixed for the life of the account.' : undefined}
          >
            {(props) => (
              <select {...props} {...register('type')} disabled={isEdit} className={SELECT_CLASS}>
                {ACCOUNT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {ACCOUNT_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Parent" error={errors.parent_id?.message} hint="Optional.">
            {(props) => (
              <select {...props} {...register('parent_id')} className={SELECT_CLASS}>
                <option value="">Top level</option>
                {parents.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.code} — {candidate.name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Description" error={errors.description?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('description')} autoComplete="off" />}
          </Field>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox className="mt-0.5" {...register('is_postable')} />
            <span>
              Entries may be posted to it
              <span className="block text-slate-500 dark:text-slate-400">
                Clear this for a heading that only totals the accounts beneath it.
              </span>
            </span>
          </label>

          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox {...register('is_active')} />
              Active — clearing this hides it from pickers without touching its history.
            </label>
          )}
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
